import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import axios from "axios";
import { Autocomplete } from "@mui/material";

interface IProps {
  closeFilterModal: () => void;
  selectedFilterAndValue: any;
  setSelectedFilterAndValue: any;
  handleSubmit: (selectedFilterValues: any) => void;
  setIsLoadingPageChange: any;
}

const CampaignFilterModel: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilterAndValue,
  setSelectedFilterAndValue,
  handleSubmit,
  setIsLoadingPageChange,
}) => {
  const [selectedCampignFilters, setSelectedTicketFilters] = useState<string[]>(
    []
  );
  const [dateTo, setDateTo] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState([]);

  const filters = [
    { label: "Customer email", value: "email__icontains" },
    { label: "Assignee", value: "assignee__username__icontains" },
    { label: "Account name", value: "account_name__icontains" },
    { label: "Date", value: "created" },
    { label: "First name", value: "first_name__icontains" },
    { label: "Last name", value: "last_name__icontains" },
    { label: "Mobile no", value: "phone_number__icontains" },
  ];

  // Load filter state from sessionStorage when the component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectedCampignFilters");
    const savedFilterValues = sessionStorage.getItem("selectedFilterAndValue");

    if (savedFilters && savedFilterValues) {
      setSelectedTicketFilters(JSON.parse(savedFilters));
      setSelectedFilterAndValue(JSON.parse(savedFilterValues));
      setDateTo(JSON.parse(savedFilterValues).created || "");
    } else {
      const appliedFilters = Object.keys(selectedFilterAndValue).filter(
        (key) => selectedFilterAndValue[key]
      );
      setSelectedTicketFilters(appliedFilters);
      setDateTo(selectedFilterAndValue.created || "");
    }
  }, [setSelectedFilterAndValue]);

  // Save filter state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem(
      "selectedCampignFilters",
      JSON.stringify(selectedCampignFilters)
    );
    sessionStorage.setItem(
      "selectedFilterAndValue",
      JSON.stringify(selectedFilterAndValue)
    );
  }, [selectedCampignFilters, selectedFilterAndValue]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedTicketFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedFilterAndValue((prev) => ({
          ...prev,
          [value]: "",
        }));
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      handleSubmit(
        updatedFilters.reduce((acc, filter) => {
          acc[filter] = selectedFilterAndValue[filter] || "";
          return acc;
        }, {})
      );
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilterAndValue((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedFilterAndValue((prev) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectedTicketFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {});
    setSelectedFilterAndValue(emptyFilters);
    setDateTo("");
    setError(null);
    sessionStorage.removeItem("selectedCampignFilters");
    sessionStorage.removeItem("selectedFilterAndValue");
    handleSubmit({});
  };

  const isFilterApplied = () =>
    selectedCampignFilters.some(
      (filter) =>
        (filter === "created" && dateTo) || selectedFilterAndValue[filter]
    );

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];
  const handleApplyFilters = () => {
    handleSubmit(selectedFilterAndValue);
    closeFilterModal();
    setIsLoadingPageChange(true);
  };

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };
  return (
    <Modal show={true} onHide={closeFilterModal} centered backdrop="static">
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={closeFilterModal} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>} {/* Display error */}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCampignFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedCampignFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            style: { textTransform: "uppercase" }, // Optional: uppercase input text
                          },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "email__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectedFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );

                          setSelectedFilterAndValue((prev) => ({
                            ...prev,
                            email__icontains: selectedSuggestion
                              ? selectedSuggestion.email
                              : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectedFilterAndValue[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CampaignFilterModel;
